import { inject, Injectable } from '@angular/core';
import { catchError, expand, map, Observable, of, reduce, ReplaySubject, switchMap, take, takeWhile, tap } from 'rxjs';
import { CrmInjectionToken } from '../tokens-and-interfaces';
import {
  CategoriesApiService,
  CategoryInterface,
  CategoryType,
  GetCategoryListRequestInterface,
  GetCategoryListResponse,
} from '@vendasta/category';

@Injectable({ providedIn: 'root' })
export class CategoryService {
  private readonly config = inject(CrmInjectionToken);
  private readonly categoriesApiService = inject(CategoriesApiService);

  allCategories$$ = new ReplaySubject<CategoryInterface[]>();

  constructor() {
    this.config.locale$
      ?.pipe(
        take(1),
        switchMap((locale) => this.fetchAllCategories(locale)),
        tap((categories) => this.allCategories$$.next(categories)),
      )
      .subscribe();
  }

  private fetchAllCategories(locale: string): Observable<CategoryInterface[]> {
    return this.fetchCategories(locale, '', 2500).pipe(
      expand((response) => this.fetchCategories(locale, response.pagingMetadata.nextCursor, 2500)),
      takeWhile((response) => !!response.pagingMetadata.hasMore, true),
      reduce(
        (all: CategoryInterface[], { category }: GetCategoryListResponse) => all.concat(category),
        [] as CategoryInterface[],
      ),
      catchError((error) => {
        console.error(error);
        return of([]);
      }),
    );
  }

  private fetchCategories(locale: string, cursor: string, pageSize: number): Observable<GetCategoryListResponse> {
    return this.categoriesApiService.getCategoryList({
      type: CategoryType.CATEGORY_TYPE_V_CATEGORY,
      pagingOptions: {
        cursor,
        pageSize,
      },
      locale: locale,
    } as GetCategoryListRequestInterface);
  }

  getMultiCategories(categoryIds: string[]): Observable<CategoryInterface[]> {
    return this.allCategories$$.pipe(
      map((categories) => {
        const mappedToCachedCategories = categoryIds.map((id) => {
          return categories.find((category) => category.externalId === id) ?? {};
        });
        return mappedToCachedCategories;
      }),
    );
  }
}
